import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: { name: string; email: string; password: string; role?: string }) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
};

// Complaints API
export const complaintsAPI = {
  create: async (complaintData: any) => {
    const response = await api.post('/complaints', complaintData);
    return response.data;
  },

  getAll: async (params?: { page?: number; limit?: number; status?: string; severity?: string; search?: string }) => {
    const response = await api.get('/complaints', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/complaints/${id}`);
    return response.data;
  },

  updateStatus: async (id: number, status: string) => {
    const response = await api.patch(`/complaints/${id}/status`, { status });
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/complaints/${id}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

// Customers API
export const customersAPI = {
  getAll: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/customers', { params });
    return response.data;
  },
};

// Products API
export const productsAPI = {
  getAll: async (params?: { page?: number; limit?: number; status?: string; search?: string; sortBy?: string; sortOrder?: string }) => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  create: async (productData: any) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  update: async (id: number, productData: any) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  getBatches: async (productId: number) => {
    const response = await api.get(`/products/${productId}/batches`);
    return response.data;
  },
};

// Batches API
export const batchesAPI = {
  getAll: async (params?: { page?: number; limit?: number; product_id?: number; search?: string }) => {
    const response = await api.get('/batches', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/batches/${id}`);
    return response.data;
  },

  create: async (batchData: any) => {
    const response = await api.post('/batches', batchData);
    return response.data;
  },

  update: async (id: number, batchData: any) => {
    const response = await api.put(`/batches/${id}`, batchData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/batches/${id}`);
    return response.data;
  },
};